import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Loader2, ExternalLink, Package, Sparkles } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AuthDialog from "@/components/AuthDialog";

export default function Billing() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [authOpen, setAuthOpen] = useState(!user);

  useEffect(() => {
    if (!user) { setAuthOpen(true); return; }
    api.get("/api/billing/me").then(({ data }) => setState(data)).catch(console.error);
  }, [user]);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/api/billing/portal");
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Portal unavailable");
      setPortalLoading(false);
    }
  };

  if (!user) return (
    <div className="max-w-[1400px] mx-auto px-6 py-24 text-center">
      <h2 className="font-heading text-3xl font-light mb-4">Sign in to view billing</h2>
      <Button onClick={() => setAuthOpen(true)} className="rounded-none bg-foreground text-background hover:bg-foreground/90">Sign in</Button>
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
    </div>
  );

  if (!state) return (
    <div className="max-w-[1400px] mx-auto px-6 py-24 flex justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
    </div>
  );

  const plan = state.subscription?.plan_id || "free";
  const status = state.subscription?.status || "active";

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-8 pt-4 md:pt-6 pb-16">
      <div className="mb-12">
        <div className="eyebrow text-muted-foreground mb-3">Billing</div>
        <h1 className="hero-title text-4xl md:text-5xl">Plan & credits.</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
        <div className="lg:col-span-2 border border-foreground p-8">
          <div className="eyebrow text-muted-foreground mb-4">Current plan</div>
          <div className="font-heading text-5xl font-light tracking-tighter capitalize mb-3" data-testid="billing-current-plan">{plan}</div>
          <div className="flex items-center gap-3 mb-6">
            <span className={`text-xs font-mono px-2 py-0.5 border ${status === "active" ? "border-foreground" : "border-destructive text-destructive"}`}>{status.toUpperCase()}</span>
            {state.subscription?.interval && <span className="text-xs font-mono text-muted-foreground">{state.subscription.interval}ly</span>}
            {state.subscription?.current_period_end && <span className="text-xs font-mono text-muted-foreground">Renews {state.subscription.current_period_end.slice(0, 10)}</span>}
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/pricing"><Button data-testid="billing-upgrade-btn" className="rounded-none bg-foreground text-background hover:bg-foreground/90">Upgrade plan</Button></Link>
            {state.subscription?.stripe_customer_id && (
              <Button data-testid="billing-portal-btn" onClick={openPortal} disabled={portalLoading} variant="outline" className="rounded-none border-foreground">
                {portalLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ExternalLink className="w-4 h-4 mr-2" />} Manage in Stripe
              </Button>
            )}
          </div>
        </div>

        <div className="border border-border p-8">
          <div className="eyebrow text-muted-foreground mb-4">Lifetime credits</div>
          <div className="flex items-baseline gap-1 mb-2">
            <span className="font-heading text-5xl font-light tracking-tighter" data-testid="billing-credits">{state.total_pack_credits}</span>
            <span className="text-xs text-muted-foreground font-mono">links</span>
          </div>
          <p className="text-xs text-muted-foreground">Consumed after plan quota. Never expire.</p>
          <Link to="/pricing" className="mt-4 inline-flex items-center text-xs link-underline" data-testid="billing-buy-pack">Buy a pack →</Link>
        </div>
      </div>

      {state.packs.length > 0 && (
        <div className="border border-border">
          <div className="p-6 border-b border-border">
            <div className="eyebrow text-muted-foreground mb-1">Purchase history</div>
            <div className="font-heading text-xl">Lifetime packs</div>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="p-4 eyebrow text-muted-foreground font-normal">Pack</th>
                <th className="p-4 eyebrow text-muted-foreground font-normal">Remaining</th>
                <th className="p-4 eyebrow text-muted-foreground font-normal">Purchased</th>
              </tr>
            </thead>
            <tbody>
              {state.packs.map((p) => (
                <tr key={p.id} className="border-b border-border last:border-0" data-testid={`pack-row-${p.pack_size}`}>
                  <td className="p-4 font-mono flex items-center gap-2"><Package className="w-3.5 h-3.5" /> Pack {p.pack_size}</td>
                  <td className="p-4 font-mono">{p.credits_remaining} / {p.pack_size}</td>
                  <td className="p-4 text-xs text-muted-foreground font-mono">{p.created_at?.slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
